import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getPhases, getProjektdokumentationTemplate } from '../services/api';
import { useDocumentStore } from '../store/documentState';
import { ProjektfreigabeStatus } from '../types/projektdokumentation';

interface Phase {
  _id: string;
  name: string;
  description: string;
  status: string;
  completionPercent?: number;
  completedAt?: string;
  completedByEmail?: string;
  approvedByEmail?: string;
  project: {
    _id: string;
    name: string;
  };
  progress?: {
    required: number;
    completed: number;
  };
}

interface PhasePickerProps {
  onError?: (error: string) => void;
  phases?: Phase[];
  useProvidedPhasesOnly?: boolean;
}

interface PhaseVisualMeta {
  eyebrow: string;
  gradient: string;
  icon: React.ReactNode;
}

interface ProjektdokumentationPhaseMeta {
  status: ProjektfreigabeStatus;
  completenessPercent: number;
  completedByEmail?: string;
  approvedByEmail?: string;
}

function getPhaseMeta(phase: Phase): PhaseVisualMeta {
  const source = `${phase._id} ${phase.name}`.toLowerCase();

  if (source.includes('projektdokumentation')) {
    return {
      eyebrow: 'Freigabe',
      gradient: 'from-cyan-500/15 via-sky-500/10 to-transparent',
      icon: (
        <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" d="M9 5h6M9 9h6m-7 8h8a2 2 0 002-2V7a2 2 0 00-2-2H8a2 2 0 00-2 2v8a2 2 0 002 2zm0 0v2m8-2v2M8 21h8" />
        </svg>
      ),
    };
  }

  if (source.includes('durchfuehrung')) {
    return {
      eyebrow: 'Baustelle',
      gradient: 'from-amber-500/15 via-orange-500/10 to-transparent',
      icon: (
        <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" d="M3 10.5L12 4l9 6.5M5 9.5V20h14V9.5M9 20v-5h6v5" />
        </svg>
      ),
    };
  }

  if (source.includes('inbetriebnahme')) {
    return {
      eyebrow: 'Live-Schaltung',
      gradient: 'from-emerald-500/15 via-teal-500/10 to-transparent',
      icon: (
        <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" d="M13 3L5 14h6l-1 7 8-11h-6l1-7z" />
        </svg>
      ),
    };
  }

  if (source.includes('abschlussbericht')) {
    return {
      eyebrow: 'Dokumentation',
      gradient: 'from-violet-500/15 via-fuchsia-500/10 to-transparent',
      icon: (
        <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" d="M8 7h8M8 11h8M8 15h5M7 3h7l5 5v11a2 2 0 01-2 2H7a2 2 0 01-2-2V5a2 2 0 012-2z" />
        </svg>
      ),
    };
  }

  if (source.includes('upload')) {
    return {
      eyebrow: 'Ablage',
      gradient: 'from-slate-500/15 via-slate-400/10 to-transparent',
      icon: (
        <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" d="M12 16V7m0 0l-3.5 3.5M12 7l3.5 3.5M5 17.5v1A1.5 1.5 0 006.5 20h11a1.5 1.5 0 001.5-1.5v-1" />
        </svg>
      ),
    };
  }

  if (source.includes('störung') || source.includes('stoerung')) {
    return {
      eyebrow: 'Issue Flow',
      gradient: 'from-rose-500/15 via-orange-500/10 to-transparent',
      icon: (
        <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" d="M12 8v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
        </svg>
      ),
    };
  }

  return {
    eyebrow: 'Phase',
    gradient: 'from-slate-500/15 via-slate-400/10 to-transparent',
    icon: (
      <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
      </svg>
    ),
  };
}

const PhasePicker: React.FC<PhasePickerProps> = ({
  onError,
  phases: providedPhases,
  useProvidedPhasesOnly = false,
}) => {
  const [phases, setPhases] = useState<Phase[]>(providedPhases || []);
  const [loading, setLoading] = useState<boolean>(!providedPhases);
  const [error, setError] = useState<string | null>(null);
  const [projektdokumentationStatuses, setProjektdokumentationStatuses] = useState<Record<string, ProjektdokumentationPhaseMeta>>({});

  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const fetchDocuments = useDocumentStore((state) => state.fetchDocuments);
  const progressMap = useDocumentStore((state) => state.progress);
  const phaseStateMap = useDocumentStore((state) => state.phases);

  useEffect(() => {
    if (useProvidedPhasesOnly) {
      setPhases(providedPhases || []);
      setLoading(false);
      return;
    }

    if (providedPhases && providedPhases.length > 0) {
      setPhases(providedPhases);
      setLoading(false);
      return;
    }

    const loadPhases = async () => {
      if (!id) {
        setError('Projekt-ID fehlt in der URL');
        onError?.('Projekt-ID fehlt in der URL');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const phasesData = await getPhases(id);
        const phasesArray = Array.isArray(phasesData) ? phasesData : [];
        setPhases(phasesArray);
        await Promise.allSettled(phasesArray.map((phase) => fetchDocuments(phase._id)));
      } catch (err: any) {
        const errorMessage = err.message || 'Fehler beim Laden der Phasen';
        setError(errorMessage);
        onError?.(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    void loadPhases();
  }, [fetchDocuments, id, onError, providedPhases, useProvidedPhasesOnly]);

  useEffect(() => {
    const projektdokumentationPhases = phases.filter((phase) => phase._id?.endsWith('-projektdokumentation'));
    if (projektdokumentationPhases.length === 0) return;

    let cancelled = false;

    const loadProjektdokumentationStatuses = async () => {
      const results = await Promise.allSettled(
        projektdokumentationPhases.map(async (phase) => {
          const projectId = phase.project?._id || phase._id.replace(/-projektdokumentation$/, '');
          let template: Awaited<ReturnType<typeof getProjektdokumentationTemplate>> | null = null;
          try {
            template = await getProjektdokumentationTemplate(projectId);
          } catch (loadError: any) {
            const message = String(loadError?.message || '');
            if (!message.includes('404')) throw loadError;
          }

          return {
            phaseId: phase._id,
            status: template?.existingRecord?.status || 'draft',
            completenessPercent: template?.existingRecord?.completenessPercent || 0,
            completedByEmail: template?.existingRecord?.completedByEmail || undefined,
            approvedByEmail: template?.existingRecord?.approvedByEmail || undefined,
          } as const;
        })
      );

      if (cancelled) return;

      const nextStatuses = results.reduce<Record<string, ProjektdokumentationPhaseMeta>>((accumulator, result) => {
        if (result.status === 'fulfilled') {
          accumulator[result.value.phaseId] = {
            status: result.value.status,
            completenessPercent: result.value.completenessPercent,
            completedByEmail: result.value.completedByEmail,
            approvedByEmail: result.value.approvedByEmail,
          };
        }
        return accumulator;
      }, {});

      setProjektdokumentationStatuses((previous) => ({ ...previous, ...nextStatuses }));
    };

    void loadProjektdokumentationStatuses();

    return () => {
      cancelled = true;
    };
  }, [phases]);

  const phaseCards = useMemo(() => {
    return phases.map((phase, index) => {
      const progress = progressMap[phase._id] || phase.progress || { completed: 0, required: 0 };
      const phaseState = phaseStateMap[phase._id];
      const status = phaseState?.status || phase.status?.toLowerCase() || 'open';
      const percentage = progress.required > 0 ? Math.round((progress.completed / progress.required) * 100) : 0;
      const isCompleted = status === 'completed';
      const isInProgress = !isCompleted && progress.completed > 0;
      const presentation = getPhaseMeta(phase);
      const dokumentationMeta = projektdokumentationStatuses[phase._id];
      const dokumentationStatus = dokumentationMeta?.status;
      const isProjektdokumentationPhase = phase._id?.endsWith('-projektdokumentation');
      const isAbschlussberichtPhase = phase._id?.endsWith('-abschlussbericht');
      const isUploadsPhase = phase._id?.endsWith('-uploads');
      const isStoerungenPhase = phase._id?.endsWith('-stoerungen');
      const hideProgressBar = isUploadsPhase || isStoerungenPhase;
      const completedByEmail = isProjektdokumentationPhase
        ? phase.completedByEmail || dokumentationMeta?.completedByEmail
        : phaseState?.completedByEmail || phase.completedByEmail || dokumentationMeta?.completedByEmail;
      const approvedByEmail = phase.approvedByEmail || dokumentationMeta?.approvedByEmail;
      const displayPercentage = isProjektdokumentationPhase
        ? dokumentationMeta?.completenessPercent || 0
        : isAbschlussberichtPhase
          ? (status === "completed" ? 100 : phase.completionPercent || 0)
        : percentage;
      const progressLabel = isProjektdokumentationPhase
        ? `${displayPercentage}%`
        : isAbschlussberichtPhase
          ? `${displayPercentage}%`
        : `${progress.completed}/${progress.required}`;
      const isCompletedByProgress =
        !hideProgressBar &&
        progress.required > 0 &&
        progress.completed >= progress.required;
      const isCompletedEffective = isCompleted || isCompletedByProgress;

      let stateLabel = 'Offen';
      let stateTone = 'bg-rose-100 text-rose-800 dark:bg-rose-500/20 dark:text-rose-200';
      let nextStep = 'Noch nichts dokumentiert.';

      if (isCompletedEffective) {
        stateLabel = 'Abgeschlossen';
        stateTone = 'bg-emerald-100 text-emerald-800 dark:bg-emerald-500/20 dark:text-emerald-200';
        nextStep = 'Alles erledigt. Nur noch bei Rückfragen oder Nachpflege öffnen.';
      } else if (
        isInProgress ||
        ((isProjektdokumentationPhase || isAbschlussberichtPhase) && displayPercentage > 0)
      ) {
        stateLabel = 'In Arbeit';
        stateTone = 'bg-amber-100 text-amber-900 dark:bg-amber-500/20 dark:text-amber-200';
        nextStep = isProjektdokumentationPhase
          ? `Checkliste ist zu ${displayPercentage}% ausgefüllt.`
          : isAbschlussberichtPhase
          ? `Abschlussbericht ist zu ${displayPercentage}% ausgefüllt.`
          : progress.required > 0
          ? `Noch ${Math.max(progress.required - progress.completed, 0)} Pflichtpunkt(e) bis zum Abschluss.`
          : 'Optionales Material und Abschlussdetails vervollständigen.';
      }

      if (dokumentationStatus === 'in_review') {
        stateLabel = 'In Prüfung';
        stateTone = 'bg-amber-100 text-amber-900 dark:bg-amber-500/20 dark:text-amber-200';
        nextStep = 'Freigabe wurde eingereicht und wartet jetzt auf Prüfung.';
      } else if (dokumentationStatus === 'ready') {
        stateLabel = 'Freigabefähig';
        stateTone = 'bg-blue-100 text-blue-800 dark:bg-blue-500/20 dark:text-blue-200';
        nextStep = 'Freigabe ist vorbereitet und kann final bestätigt werden.';
      } else if (dokumentationStatus === 'approved') {
        stateLabel = 'Freigegeben';
        stateTone = 'bg-emerald-100 text-emerald-800 dark:bg-emerald-500/20 dark:text-emerald-200';
        nextStep = 'Interne Freigabe liegt vor. Diese Phase ist sauber abgeschlossen.';
      }

      if (!isCompletedEffective && hideProgressBar) {
        nextStep = isUploadsPhase
          ? 'Hier gibt es keine Pflichtdokumente. Lade Inhalte bei Bedarf hoch.'
          : 'Hier gibt es keine Pflichtdokumente. Erfasse Störungen und Nacharbeiten bei Bedarf.';
      }

      return {
        phase,
        index,
        progress,
        percentage: displayPercentage,
        progressLabel,
        isCompleted: isCompletedEffective,
        isInProgress,
        presentation,
        stateLabel,
        stateTone,
        nextStep,
        hideProgressBar,
        completedByEmail,
        approvedByEmail,
      };
    });
  }, [phaseStateMap, phases, progressMap, projektdokumentationStatuses]);

  const recommendedPhaseId = useMemo(() => {
    const candidate = phaseCards.find((card) => !card.isCompleted);
    return candidate?.phase._id || null;
  }, [phaseCards]);

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="h-12 w-12 animate-spin rounded-full border-4 border-slate-200 border-t-cyan-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-[24px] border border-red-200 bg-red-50 p-4 text-red-800 dark:border-red-400/40 dark:bg-red-950/40 dark:text-red-200">
        {error}
      </div>
    );
  }

  if (phases.length === 0) {
    return (
      <div className="rounded-[28px] border border-slate-200/70 bg-white/95 p-8 text-center shadow-sm dark:border-white/10 dark:bg-slate-900/80 dark:shadow-[0_20px_60px_rgba(2,6,23,0.45)]">
        <h3 className="text-lg font-semibold text-slate-950 dark:text-slate-100">Keine Phasen gefunden</h3>
        <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
          Für dieses Projekt wurden keine Phasen definiert.
        </p>
        <button
          onClick={() => navigate('/projects', { replace: true })}
          className="mt-5 rounded-full bg-slate-900 px-5 py-3 text-sm font-semibold text-white dark:bg-slate-100 dark:text-slate-900"
        >
          Zurück zum Projekt
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="grid gap-5 pb-6 lg:grid-cols-2">
        {phaseCards.map((card) => {
          const phaseId = card.phase._id || `phase-${card.index}`;
          const isRecommended = phaseId === recommendedPhaseId;
          const projectId = card.phase.project?._id || id;

          return (
            <button
              key={phaseId}
              data-testid="phase-card"
              data-phase-id={phaseId}
              onClick={(event) => {
                event.preventDefault();
                if (!projectId || !phaseId) {
                  onError?.('Ungültige Projekt- oder Phasen-ID');
                  return;
                }
                navigate(`/projects/${projectId}/phase/${phaseId}`, { replace: true });
              }}
              className={`group relative overflow-hidden rounded-[30px] border bg-white/95 p-6 text-left shadow-[0_24px_80px_rgba(15,23,42,0.08)] transition hover:-translate-y-0.5 hover:shadow-[0_30px_90px_rgba(15,23,42,0.12)] dark:bg-slate-900/80 dark:shadow-[0_24px_70px_rgba(2,6,23,0.5)] dark:hover:shadow-[0_30px_90px_rgba(2,6,23,0.6)] ${isRecommended ? 'border-amber-300 ring-1 ring-amber-200 dark:border-amber-300/70 dark:ring-amber-300/30' : 'border-slate-200/70 dark:border-white/10'}`}
            >
              <div className={`absolute inset-0 bg-gradient-to-br ${card.presentation.gradient} pointer-events-none`} />
              <div className="relative">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div className="flex min-w-0 items-start gap-4">
                    <div className="flex h-14 w-14 items-center justify-center rounded-2xl border border-slate-200/70 bg-white/90 text-slate-900 shadow-sm dark:border-white/10 dark:bg-slate-950/85 dark:text-slate-100">
                      {card.presentation.icon}
                    </div>
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 dark:text-slate-400">
                          {card.presentation.eyebrow}
                        </span>
                        {isRecommended && !card.isCompleted && (
                          <span className="rounded-full bg-slate-950 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-white dark:bg-slate-100 dark:text-slate-900">
                            Empfohlen
                          </span>
                        )}
                      </div>
                      <h3 className="mt-2 break-words text-xl font-semibold text-slate-950 dark:text-slate-100">
                        {card.phase.name || 'Unbenannte Phase'}
                      </h3>
                      <p className="mt-2 max-w-xl text-sm leading-6 text-slate-600 dark:text-slate-300">
                        {card.phase.description || 'Keine Beschreibung verfügbar.'}
                      </p>
                    </div>
                  </div>

                  <span className={`inline-flex self-start shrink-0 items-center rounded-full px-3 py-1.5 text-xs font-semibold sm:self-auto ${card.stateTone}`}>
                    {card.stateLabel}
                  </span>
                </div>

                <div className="mt-6 grid gap-4 sm:grid-cols-[1fr_auto] sm:items-end">
                  <div>
                    {!card.hideProgressBar && (
                      <>
                        <div className="flex items-center justify-between text-sm text-slate-500 dark:text-slate-300">
                          <span>Pflichtfortschritt</span>
                          <span className="font-semibold text-slate-900 dark:text-slate-100">{card.progressLabel}</span>
                        </div>
                        <div className="mt-2 h-2.5 rounded-full bg-slate-200 dark:bg-slate-700">
                          <div
                            className="h-2.5 rounded-full bg-gradient-to-r from-cyan-500 to-orange-500"
                            style={{ width: `${card.percentage > 0 ? card.percentage : 8}%` }}
                          />
                        </div>
                      </>
                    )}
                    <p className="mt-3 text-sm text-slate-600 dark:text-slate-300">{card.nextStep}</p>
                    {card.isCompleted && (card.completedByEmail || card.approvedByEmail) && (
                      <div className="mt-3 space-y-1 text-xs text-slate-500 dark:text-slate-400">
                        {card.completedByEmail && (
                          <p>Abgeschlossen von: {card.completedByEmail}</p>
                        )}
                        {card.approvedByEmail && (
                          <p>Freigegeben von: {card.approvedByEmail}</p>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="flex items-center gap-2 text-sm font-semibold text-slate-900 dark:text-slate-100">
                    Phase öffnen
                    <svg className="h-4 w-4 transition group-hover:translate-x-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default PhasePicker;
